import React, { ReactElement, useMemo, useState, useCallback, useRef } from "react";
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
} from "src/components/Drawer";
import { QualityHubVerificationDrawerProps, QualityHubVerificationDrawerState } from "./QualityHubVerificationDrawer.types";
import { useSelector } from "react-redux";
import { localize } from "src/l10n";
import { SpintrTypes } from "src/typings";
import { SmallBody, SmallBodyBolder } from "../Text";
import { UnstyledButton } from "src/ui";
import { PrimaryButton } from "@fluentui/react";

/**
 * QualityHubVerificationDrawer component
 *
 * @param {QualityHubVerificationDrawerProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubVerificationDrawer({
    onDismiss,
    onVerificationSubmitted,
    open,
    verificationTypes,
}: QualityHubVerificationDrawerProps): ReactElement | null {
    const [state, setState] = useState<QualityHubVerificationDrawerState>({
        files: [],
    });

    const inputRef = useRef<HTMLInputElement>(null);

    const isSmallViewMode = useSelector<Spintr.AppState, boolean>(
        (appState) => appState.ui.isSmallViewMode,
    );

    const l10n = useMemo(() => ({
        drawerTitle: localize("VERIFICATION_REQUIRED"),
        infoText: localize("VERIFICATION_INFO_TEXT"),
        cancel: localize("Avbryt"),
        submitText: localize("SUBMIT_VERIFICATION"),
    }), []);

    const { allowImage, allowVideo, } = useMemo(() => ({
        allowImage: (verificationTypes & SpintrTypes.QualityHubVerificationType.Image) === SpintrTypes.QualityHubVerificationType.Image,
        allowVideo: (verificationTypes & SpintrTypes.QualityHubVerificationType.Video) === SpintrTypes.QualityHubVerificationType.Video,
    }), [verificationTypes]);

    const accept = useMemo(() => {
        const types: string[] = [];
        if (allowImage) {
            types.push("image/*");
        }
        if (allowVideo) {
            types.push("video/*");
        }
        return types.join(",");
    }, [allowImage, allowVideo]);

    const onCancelClicked = useCallback(() => onDismiss?.(), []);

    const onSubmitting = useCallback(() => {
        if (!onVerificationSubmitted) {
            return;
        }

        onVerificationSubmitted(state.files.map((f) => f.file));
    }, [onVerificationSubmitted, state.files]);

    const onFilesSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = (event.target.files ? Array.from(event.target.files) : [])
            .filter((file) => {
                if (allowImage && file.type.startsWith("image/")) {
                    return true;
                }

                if (allowVideo && file.type.startsWith("video/")) {
                    return true;
                }

                return false;
            })
            .map((file) => ({
                file,
                previewUrl: URL.createObjectURL(file),
            }));

        setState((prevState) => ({
            ...prevState,
            files: [...prevState.files, ...files],
        }));

    }, [allowImage, allowVideo]);

    return (
        <Drawer className="QualityHubVerificationDrawer"
                open={open}
                position="end"
                size={isSmallViewMode ? "full" : "medium"}>
            <DrawerHeader>
                <DrawerHeaderTitle>
                    {l10n.drawerTitle}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody className="QualityHubVerificationDrawer-body">
                <div className="info-text">
                    <SmallBody>
                        {l10n.infoText}
                    </SmallBody>
                </div>
                <div className="buttons">
                    <PrimaryButton onClick={() => inputRef.current?.click()}>
                        {localize("ValjFil")}
                    </PrimaryButton>
                    <input accept={accept}
                           capture="environment"
                           multiple={true}
                           name="files"
                           onChange={onFilesSelected}
                           ref={inputRef}
                           type="file" />
                </div>
                <div className="files">
                    {state.files.map((upload, index) => (
                        <div className="file" key={index}>
                            <img alt={upload.file.name} src={upload.previewUrl} />
                        </div>
                    ))}
                </div>
            </DrawerBody>
            <DrawerFooter className="QualityHubVerificationDrawer-footer">
                <UnstyledButton onClick={onCancelClicked}>
                    <SmallBodyBolder color="contentLight">
                        {l10n.cancel}
                    </SmallBodyBolder>
                </UnstyledButton>
                <PrimaryButton className="QualityHubTaskFormDrawer-save"
                               disabled={state.files.length === 0}
                               onClick={onSubmitting}
                               type="submit">
                    {l10n.submitText}
                </PrimaryButton>
            </DrawerFooter>
        </Drawer>
    );
}

export default QualityHubVerificationDrawer;

